import '@fe-toolkit/web-components/fonts.css'
import '@fe-toolkit/web-components/base-styles.css'
import '@giffgaff/fe-design-system-fonts/fonts.css'
import { defineRuntimeConfig } from '@modern-js/runtime'
import { observabilityRuntimePlugin } from '@fe-toolkit/observability/modern'
import { parseObservabilityEnvironment } from '@fe-toolkit/observability/client'

import { currentRouterBasename } from '@/config/routerBasename'
import { basePath, mfeName } from '@/config/runtimeConfig'

import { version } from '../package.json'

// Override webpack's public path for CDN-enabled environments so that lazy-loaded
// route chunks (dynamic imports) are fetched from the CDN rather than the pod.
//
// Background: `output.assetPrefix` in modern.config.ts is a BUILD-TIME value. With
// "build once, deploy anywhere", CDN_URL is not set during CI so the bundle bakes in
// `__webpack_require__.p = '/'`. This means all lazy chunks would load from the pod
// even when CDN is enabled.
//
// Fix: update __webpack_public_path__ (rspack's alias for __webpack_require__.p) early
// in the entry module. window.__RUNTIME_CONFIG__ is already available here because
// src/routes/layout.tsx renders it as an inline <script> *before* <Scripts /> in
// Document.tsx, so it is set in the SSR HTML before the main bundle executes.
//
// The assignment must happen before any dynamic import() is triggered. Since Modern.js
// router loads all routes lazily, this module-init assignment is always early enough.
if (typeof window !== 'undefined') {
  const cfg = window.__RUNTIME_CONFIG__

  if (cfg?.CDN_URL && mfeName) {
    const cdnBase = cfg.CDN_URL.replace(/\/$/, '')

    // __webpack_public_path__ is a magic rspack/webpack variable. Inside a bundled
    // module, rspack compiles this assignment to __webpack_require__.p = ...
    // The trailing slash is required by the webpack public path convention.
    __webpack_public_path__ = `${cdnBase}/${mfeName}/`
  }
}

// MSW is only included when ENABLE_MOCKS=true is set at BUILD TIME (or in dev).
//
// Using build-time process.env.ENABLE_MOCKS (exposed via globalVars in modern.config.ts)
// allows Rspack to statically eliminate the dynamic import expression when mocking is
// disabled. This prevents the MSW/faker/testing chunk (~762KB) from being bundled or
// preloaded in production builds, which was a significant TBT contributor.
//
// For E2E test builds that need browser-side mocking: run `ENABLE_MOCKS=true pnpm build`.
// Runtime-only ENABLE_MOCKS (window.__RUNTIME_CONFIG__) cannot drive this import because
// Rspack evaluates the module graph at build time — a runtime-only guard still causes the
// chunk to be generated and added to the SSR HTML preload list.
if ((process.env.NODE_ENV === 'development' || process.env.ENABLE_MOCKS === 'true') && typeof window !== 'undefined') {
  import('../shared/msw/browser').then(({ enableMocking, worker }) => {
    /*
     * Matches `server.publicRoutes`. `basePath` is the pod's serving prefix, not
     * `currentRouterBasename()`, which answers what the browser's URL bar shows and is
     * `undefined` on the Portal-proxied `/` — the broken root URL this replaces.
     *
     * Only fixes direct `/landing-ms` access: local dev and the CI E2E run. Behind Portal's
     * proxy the worker still cannot control a page the browser sees at `/`.
     *
     * The scope is `basePath` rather than the default `basePath/` (the worker's folder) so
     * that it covers the homepage at `basePath` itself; the `msw-worker-scope` middleware in
     * server/modern.server.ts sends the `Service-Worker-Allowed` header that permits it. Under the default scope the homepage
     * was never controlled, and a second homepage visit in the same browser could catch
     * MSW's "registered but not controlling, reload" check before the previous page's worker
     * had unregistered itself, then reload in a loop until `page.goto` timed out (FAAS-1643).
     * `enableMocking` takes no scope, so it goes in through `worker.start`.
     */
    const start = worker.start.bind(worker)

    worker.start = (options) =>
      start({ ...options, serviceWorker: { ...options?.serviceWorker, options: { scope: basePath } } })
    enableMocking(worker, { serviceWorkerUrl: `${basePath}/mockServiceWorker.js` })
  })
}

// The deployment environment (local | tst | stg | prd) drives everything: @fe-toolkit/observability
// resolves the traces/metrics/logs collector URLs from it, so no URL is configured here, and stamps
// it on every span, log record and metric event as `deployment.environment`. An unknown value is
// warned about and treated as "not set". No environment, no export: with every enable flag false,
// the plugin's setupObservability call is a no-op. Its setup runs at bootstrap before any route
// module loads, so module-level createLogger() calls still bind the real LoggerProvider.
const runtimeConfig = typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ : undefined
const deploymentEnvironment = parseObservabilityEnvironment(runtimeConfig?.ENVIRONMENT, 'ENVIRONMENT')
const tracingEnabled = Boolean(deploymentEnvironment) && runtimeConfig?.ENABLE_TRACING === 'true'
const observabilityEnabled = Boolean(deploymentEnvironment)

// See src/config/routerBasename.ts: the browser is not always on the path the pod served,
// because Portal proxies the public `/` to this app's `/landing-ms/` (FAAS-909).
export default defineRuntimeConfig({
  plugins: [
    // Also wraps the app root in an ErrorBoundary, which a module-init setupObservability
    // call cannot do. Error tracking emits logs, so it needs enableLogs on too.
    observabilityRuntimePlugin({
      mfeName: mfeName ? `${mfeName}-client` : 'unknown-mfe',
      version: version || '0.0.0',
      environment: deploymentEnvironment,
      enableMetrics: observabilityEnabled,
      enableLogs: observabilityEnabled,
      enableErrorTracking: observabilityEnabled,
      enableTracing: tracingEnabled,
      // Becomes the client-errors metric's team_name label, which alert routing keys on.
      // Matches team_name in .github/workflows/caller-workflow.yaml.
      teamName: 'faas',
    }),
  ],
  router: {
    basename: currentRouterBasename(),
  },
})
